import { HighlightOff, PlaylistAddCheck } from "@mui/icons-material";
import { Badge } from "@mui/material";
import { useMotionValue } from "motion/react";
import { FC, ReactElement, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import {
  useCrudEditBulk,
  useDynamicFormsBulkActions,
  useModalConfirm,
  usePageContext,
  useSelectedIds,
} from "@ryde/redux";
import {
  ActionsEnum,
  ConfirmResult,
  dynamicFormBulkUpdateControlsSF,
  isEntityWithoutBulkDeleteAction,
  PLURAL_KEY,
  useHasPermissionCustomField,
  useHasPermissions,
  useIsMobile,
} from "@ryde/shared";
import { greyMui } from "@ryde/theme";

import { MuiButton } from "../../controls";
import { AccessControl } from "../access-control";
import { ExpandFloatingMenuFlow } from "./expand-floating-menu";
import { FloatingMenuButton, FloatingMenuStyled } from "./floating-menu.styled";
import { FloatingMenuTooltipWrapper } from "./floating-menu.tooltip";
import { useFloatMenu } from "./use-float-menu";

interface IFloatingMenuGridProps {
  elements?: ReactElement;
}

export const FloatingMenuGrid: FC<IFloatingMenuGridProps> = ({
  elements,
}: IFloatingMenuGridProps) => {
  const { t } = useTranslation();
  const { entity } = usePageContext();
  const { isMobile } = useIsMobile();
  const { openBulkEditForm } = useCrudEditBulk(entity);
  const { confirmDialogResult } = useModalConfirm();
  const { removeSelectedIds } = useSelectedIds();
  const { bulkArchive } = useDynamicFormsBulkActions(entity);
  const { getFieldPermissions } = useHasPermissionCustomField();
  const { hasPermissions: hasUpdatePermissions } = useHasPermissions({
    entity,
    action: "update",
  });

  const { selectedRowsIds: selectedIds } = useSelectedIds();
  const isOpened = Boolean(selectedIds.length);
  const {
    isInitFinished,
    leftX,
    bottomY,
    setLeftX,
    setBottomY,
    floatingMenuRef,
  } = useFloatMenu({ isOpened, page: "common" });

  const left = useMotionValue(leftX);
  const bottom = useMotionValue(bottomY);
  const filteredControls = useRecoilValue(
    dynamicFormBulkUpdateControlsSF(entity),
  );
  const hasAnyBulkUpdateField = Boolean(filteredControls.length);

  const isBulkUpdateAvailable = useMemo(() => {
    if (!hasAnyBulkUpdateField) {
      return false;
    }

    return (
      filteredControls.some(
        ctrl =>
          !getFieldPermissions({
            ...ctrl,
            formEntity: entity,
          }).isFieldDisabled,
      ) || hasUpdatePermissions
    );
  }, [
    entity,
    filteredControls,
    hasUpdatePermissions,
    hasAnyBulkUpdateField,
    getFieldPermissions,
  ]);

  const handleArchiveClick = useEvent(async () => {
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.ARCHIVE_ITEMS_MESSAGE`),
    });

    if (confirmationResult === ConfirmResult.OK) {
      await bulkArchive(selectedIds);
    }
  });

  const handleUpdateClick = useEvent(() => {
    openBulkEditForm();
  });

  // Clear only the currently selected (effective) ids, not the whole grid selection.
  const handleClearSelected = useEvent(() => {
    removeSelectedIds(selectedIds);
  });

  const onDragEndHandler = useEvent((): void => {
    setLeftX(left.get());
    setBottomY(bottom.get());
  });

  useEffect(() => {
    if (!isOpened) {
      return;
    }

    left.set(leftX);
  }, [isOpened, left, leftX]);

  return (
    isOpened && (
      <FloatingMenuStyled
        ref={floatingMenuRef}
        drag
        dragElastic={0}
        dragMomentum={false}
        onDragEnd={onDragEndHandler}
        style={{ x: left, bottom, opacity: !isInitFinished ? 0 : "initial" }}
      >
        {!isMobile && <SVG_MUI.GripVertical3SVG className="pointer grip" />}

        <Badge color="secondary" badgeContent={selectedIds?.length}>
          <PlaylistAddCheck style={{ color: greyMui._700 }} />
        </Badge>

        <FloatingMenuTooltipWrapper
          tooltip={t("general.CLEAR_BULK", {
            entity: t(`${entity}.${PLURAL_KEY}`),
          })}
        >
          <FloatingMenuButton onClick={handleClearSelected}>
            <HighlightOff sx={{ color: greyMui._700 }} />
          </FloatingMenuButton>
        </FloatingMenuTooltipWrapper>
        {elements}
        {!isEntityWithoutBulkDeleteAction(entity) && (
          <AccessControl entity={entity} action={ActionsEnum.Delete}>
            <FloatingMenuButton onClick={handleArchiveClick}>
              {!isMobile && <SVG_MUI.ArchiveSVG />}
              <span>{t(`general.ARCHIVE`)}</span>
            </FloatingMenuButton>
          </AccessControl>
        )}

        {isBulkUpdateAvailable && (
          <MuiButton sx={{ minWidth: 100 }} onClick={handleUpdateClick}>
            {t(`general.BULK_UPDATE`)}
          </MuiButton>
        )}

        {/* EXPAND MENU BY ENTITY */}
        <ExpandFloatingMenuFlow />
      </FloatingMenuStyled>
    )
  );
};
